"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import { AgentBubble } from "@/components/agents/AgentBubble";
import type { AgentLine } from "@/lib/sse/useSSE";
import { isPublic } from "@/lib/profile";
import { PrivateOnlyMark } from "@/components/scene/PrivateOnlyMark";
import { useTranslations } from "@/lib/compat/i18n";

const BUBBLE_TTL_MS = 6500;

/**
 * Floating agent bubble layer. Renders the most recent agent.Spoke events
 * for ~6.5 s each, anchored to the bottom-end corner of the screen.
 *
 * Per design principle 6: agents speak in 1 sentence max per appearance.
 * Bubbles fade out to keep the chrome clean and prevent the audience from
 * reading them as "narration."
 */
export function AgentLayer({ lines, className }: { lines: AgentLine[]; className?: string }) {
  const t = useTranslations("agentLines");
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, []);

  // Sage is implemented internally (its policy.Decided events still flow
  // through the SSE stream for audit) but its bubbles are filtered out of
  // the audience-facing AgentLayer per the Tier D compliance-thread scrub.
  // On the public profile Vega is also suppressed — its compliance persona
  // is hidden from the public audience.
  const suppressPublicVega = isPublic();
  const visible = lines
    .filter((l) => l.agent !== "sage")
    .filter((l) => !(suppressPublicVega && l.agent === "vega"))
    .filter((l) => now - l.occurredAt < BUBBLE_TTL_MS)
    .slice(-3);

  // Publish "agent has active bubbles" state on <html> so IdleHint can
  // suppress itself via CSS (see globals.css). Pure side effect — IdleHint
  // doesn't need a prop or a context to know about us.
  useEffect(() => {
    if (visible.length > 0) {
      document.documentElement.dataset.agentActive = "true";
    } else {
      delete document.documentElement.dataset.agentActive;
    }
    return () => { delete document.documentElement.dataset.agentActive; };
  }, [visible.length]);

  return (
    <div
      data-component="agent-layer"
      className={clsx("pointer-events-none fixed bottom-6 z-40 flex flex-col gap-2 transition-opacity duration-state ease-state", "start-6", className)}
    >
      {visible.map((l) => {
        const age = now - l.occurredAt;
        const opacity = age < 5500 ? 1 : Math.max(0, 1 - (age - 5500) / 1000);
        const displayLine = l.lineKey ? t(l.lineKey, { fallback: l.line }) : l.line;
        const bubble = <AgentBubble agent={l.agent as "echo" | "vega" | "atlas" | "mercator" | "sage" | "eva"} line={displayLine} />;
        return (
          <div key={l.id} style={{ opacity }} className="transition-opacity duration-state ease-state">
            {l.agent === "vega" ? <PrivateOnlyMark>{bubble}</PrivateOnlyMark> : bubble}
          </div>
        );
      })}
    </div>
  );
}
